import { useState, useEffect, createRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { useUserContext } from 'contexts/UserContext';
import { SearchIc } from '@dsch/dd-icons';

import { media } from 'helpers/breakpoints';

import { Button } from 'components/Toolkit/Button/Button';
import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { LinkButton } from 'components/Toolkit/Button/LinkButton';
import { useGlobalContext } from 'contexts/GlobalContext';
import { VerifyTooltip } from 'components/PageWrappers/SharedComponents/VerifyTooltip/VerifyTooltip';

import { BaseHeader } from 'components/Layouts/Headers/Shared/BaseHeader';
import { HeaderUserProfile } from 'components/Layouts/Headers/HeaderComponents/HeaderUserProfile';
import { HeaderDropdown } from 'components/Layouts/Headers/HeaderComponents/HeaderDropdown';
import { useAuthOptions } from 'hooks/useAuthOptions';

type HeaderVariant = 'DEFAULT' | 'TRANSPARENT';

export interface StandardHeaderProps {
  /** Current logged in user's name */
  username?: string;
  /** Current base url of application */
  baseUrl: string;
  /** Cdn URl */
  cdnUrl: string;
  /** Count of notifications for user */
  notificationCount?: number;
  /** Count of unread messages for user */
  unreadMessageCount?: number;
  /** Count of unread history checks for user */
  unreadHistoryCheckCount?: number;
  /** Header variants */
  variant?: HeaderVariant;
  /** Has box shadow */
  headerHasShadow?: boolean;
  /** Has spacer to push content lower */
  headerHasSpacer?: boolean;
  vertical?: string;
}

const Wrapper = styled.div<{
  variant: HeaderVariant;
  hasShadow?: boolean;
  hasSpacer?: boolean;
}>`
  position: relative;
  z-index: 9000;
  margin-bottom: ${({ hasSpacer, theme }) =>
    hasSpacer ? theme.spacing.M24 : 0};
  background: ${({ theme, variant }) =>
    variant === 'TRANSPARENT' ? 'transparent' : theme.colors.WHITE};
  ${({ variant }) => variant === 'TRANSPARENT' && 'margin-bottom: -72px'};
  ${({ theme, hasShadow }) =>
    hasShadow && `box-shadow: ${theme.shadow.CONTAINER}`};
  .desktop-only {
    display: none;
    @media (min-width: 840px) {
      display: initial;
    }
  }
`;

const PlaceAdButton = styled.div`
  width: 80px;
  @media (min-width: 840px) {
    width: 140px;
  }
`;

const SearchWrapper = styled.div<{ variant: HeaderVariant }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover,
  &:active {
    box-shadow: inset 0 -1px 0 0 ${({ theme, variant }) => (variant === 'TRANSPARENT' ? theme.colors.WHITE : theme.colors.GREY_DARKER)};
  }
`;

const SearchIcon = styled(SearchIc)`
  ${media.large} {
    margin-right: ${({ theme }) => theme.spacing.S4};
  }
`;

const SearchText = styled.p<{ variant: HeaderVariant }>`
  display: none;
  ${media.large} {
    display: block;
    ${({ theme }) => theme.fontSize.M14};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    color: ${({ theme, variant }) =>
      variant === 'TRANSPARENT'
        ? theme.colors.WHITE
        : theme.colors.GREY_DARKER};
  }
`;

const SHeaderUserProfile = styled(HeaderUserProfile)<{
  isNotLoggedIn: boolean;
}>`
  margin-left: ${({ theme, isNotLoggedIn }) =>
    isNotLoggedIn ? theme.spacing.M16 : theme.spacing.S8};
`;

function StandardHeader({
  username = '',
  notificationCount,
  baseUrl,
  cdnUrl,
  unreadMessageCount,
  unreadHistoryCheckCount,
  variant = 'DEFAULT',
  headerHasShadow = false,
  headerHasSpacer = false,
  vertical = '',
}: StandardHeaderProps) {
  const theme = useTheme();
  const { user } = useUserContext();
  const [posX, setPosX] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const headerDropDownRef = createRef<HTMLDivElement>();
  const headerUserProfileRef = createRef<HTMLButtonElement>();
  const {
    handleLogin,
    handleSignup,
    handleLogout,
    isAuthenticated,
    legacyAuth,
  } = useAuthOptions();

  const { setIsGlobalSearchModalOpen } = useGlobalContext();

  function handleClick(event: React.MouseEvent) {
    event.stopPropagation();
    setIsActive(!isActive);
  }

  function handleActive() {
    setIsOpen(!isOpen);
  }

  function handlePosX(xCoordinate: number) {
    let fullWidth = window.innerWidth;
    setPosX(fullWidth - xCoordinate);
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleClickOutside);
    };
  }, [isOpen]);

  function handleClickOutside(event: MouseEvent | KeyboardEvent) {
    if (
      (headerDropDownRef.current &&
        headerDropDownRef.current.contains(event.target as Node)) ||
      (headerUserProfileRef.current &&
        headerUserProfileRef.current.contains(event.target as Node))
    ) {
      return;
    }
    setIsActive(false);
    setIsOpen(false);
  }

  return (
    <Wrapper
      variant={variant}
      hasShadow={headerHasShadow}
      hasSpacer={headerHasSpacer}
    >
      <div className="row">
        <div className="columns small-12">
          <BaseHeader
            baseUrl={baseUrl}
            vertical={vertical}
            cdnUrl={cdnUrl}
            variant={variant}
          >
            <SearchWrapper
              onClick={() => setIsGlobalSearchModalOpen(true)}
              variant={variant}
            >
              <SearchIcon
                data-testid="global-search-icon"
                color={
                  variant === 'TRANSPARENT'
                    ? theme.colors.WHITE
                    : theme.colors.GREY_DARKER
                }
              />
              <SearchText variant={variant}>Search</SearchText>
            </SearchWrapper>
            <PlaceAdButton>
              {user && !user?.isMinimumVerified ? (
                <VerifyTooltip
                  arrowPosition={['right', 'right', 'center']}
                  tooltipPosition={'below'}
                >
                  <Button
                    ofType="GREEN"
                    data-tracking="menu-bar-place-ad-button"
                  >
                    Place Ad
                  </Button>
                </VerifyTooltip>
              ) : (
                <ButtonLink
                  ofType="GREEN"
                  href={`${baseUrl}/publish`}
                  data-tracking="menu-bar-place-ad-button"
                >
                  Place Ad
                </ButtonLink>
              )}
            </PlaceAdButton>
            {!isAuthenticated ? (
              <>
                {legacyAuth ? (
                  <>
                    <LinkButton
                      ofType="TERTIARY"
                      className="desktop-only"
                      onClick={handleLogin}
                      data-testid="desktop-login"
                      data-tracking="menu-bar-login"
                      colorVariant={
                        variant === 'TRANSPARENT' ? 'WHITE' : 'GREY_DARKER'
                      }
                      underlineOnHover={true}
                    >
                      Log In
                    </LinkButton>
                    <LinkButton
                      ofType="TERTIARY"
                      className="desktop-only"
                      onClick={handleSignup}
                      data-testid="desktop-signup"
                      data-tracking="menu-bar-signup"
                      colorVariant={
                        variant === 'TRANSPARENT' ? 'WHITE' : 'GREY_DARKER'
                      }
                      underlineOnHover={true}
                    >
                      Sign Up
                    </LinkButton>
                  </>
                ) : (
                  <LinkButton
                    ofType="TERTIARY"
                    className="desktop-only"
                    onClick={handleLogin}
                    data-testid="desktop-login"
                    data-tracking="menu-bar-login"
                    colorVariant={
                      variant === 'TRANSPARENT' ? 'WHITE' : 'GREY_DARKER'
                    }
                    underlineOnHover={true}
                  >
                    Log in
                  </LinkButton>
                )}
              </>
            ) : null}
            <SHeaderUserProfile
              ref={headerUserProfileRef}
              username={username}
              profileImage={`${cdnUrl}/images/illustrations/profile.svg`}
              notificationCount={notificationCount}
              posX={handlePosX}
              handleClick={handleClick}
              isActive={isActive}
              handleActive={handleActive}
              isNotLoggedIn={!isAuthenticated}
              variant={variant}
            />
            <HeaderDropdown
              ref={headerDropDownRef}
              username={username}
              isOpen={isOpen}
              baseUrl={baseUrl}
              cdnUrl={cdnUrl}
              unreadMessageCount={unreadMessageCount}
              unreadHistoryCheckCount={unreadHistoryCheckCount}
              posX={posX}
              setIsOpen={setIsOpen}
              onLogin={handleLogin}
              onSignup={handleSignup}
              legacyAuth={legacyAuth}
              logout={handleLogout}
            />
          </BaseHeader>
        </div>
      </div>
    </Wrapper>
  );
}

export { StandardHeader };
